/**
 * Google Analytics 4 utility for tracking page views and events
 * Measurement ID: G-VDDVX83KCC
 */

const GA_MEASUREMENT_ID = 'G-VDDVX83KCC';

// Check if gtag is available
const isGtagAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Track a page view (for SPA navigation)
 * @param {string} url - The URL to track
 * @param {string} title - The page title
 */
export const trackPageView = (url, title) => {
  if (!isGtagAvailable()) return;
  
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: url,
    page_title: title,
    page_location: window.location.origin + url
  });
};

/**
 * Track a custom event
 * @param {string} action - Event action (e.g., 'click', 'submit', 'view')
 * @param {string} category - Event category (e.g., 'booking', 'gallery', 'payment')
 * @param {string} label - Event label (optional)
 * @param {number} value - Event value (optional)
 */
export const trackEvent = (action, category, label = null, value = null) => {
  if (!isGtagAvailable()) return;
  
  const eventParams = {
    event_category: category,
  };
  
  if (label) eventParams.event_label = label;
  if (value !== null) eventParams.value = value;
  
  window.gtag('event', action, eventParams);
};

/**
 * Track user login
 * @param {string} method - Login method (e.g., 'google', 'email')
 */
export const trackLogin = (method) => {
  if (!isGtagAvailable()) return;
  
  window.gtag('event', 'login', {
    method: method
  });
};

/**
 * Track user signup
 * @param {string} method - Signup method
 */
export const trackSignUp = (method) => {
  if (!isGtagAvailable()) return;
  
  window.gtag('event', 'sign_up', {
    method: method
  });
};

/**
 * Track booking request
 * @param {string} eventType - Type of event (e.g., 'wedding', 'portrait')
 * @param {string} photographerId - Photographer ID
 */
export const trackBookingRequest = (eventType, photographerId) => {
  if (!isGtagAvailable()) return;
  
  window.gtag('event', 'generate_lead', {
    event_category: 'booking',
    event_label: eventType,
    photographer_id: photographerId
  });
};

/**
 * Track payment
 * @param {string} transactionId - Transaction/invoice ID
 * @param {number} value - Payment amount
 * @param {string} currency - Currency code (default: USD)
 */
export const trackPayment = (transactionId, value, currency = 'USD') => {
  if (!isGtagAvailable()) return;
  
  window.gtag('event', 'purchase', {
    transaction_id: transactionId,
    value: value,
    currency: currency
  });
};

/**
 * Track gallery view
 * @param {string} galleryId - Gallery ID
 * @param {string} galleryTitle - Gallery title
 */
export const trackGalleryView = (galleryId, galleryTitle) => {
  if (!isGtagAvailable()) return;
  
  window.gtag('event', 'view_item', {
    event_category: 'gallery',
    event_label: galleryTitle,
    gallery_id: galleryId
  });
};

/**
 * Track photo download
 * @param {string} galleryId - Gallery ID
 * @param {number} photoCount - Number of photos downloaded
 */
export const trackPhotoDownload = (galleryId, photoCount = 1) => {
  if (!isGtagAvailable()) return;
  
  window.gtag('event', 'download', {
    event_category: 'gallery',
    event_label: galleryId,
    value: photoCount
  });
};

export default {
  trackPageView,
  trackEvent,
  trackLogin,
  trackSignUp,
  trackBookingRequest,
  trackPayment,
  trackGalleryView,
  trackPhotoDownload
};
